<script lang="ts" setup>
    import BaseInput from '@/components/BaseInput.vue';
    import BaseToggleButton from '@/components/BaseToggleButton.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import IconCharacterSpacing from '@/components/icons/IconCharacterSpacing.vue';
    import IconLineSpacing from '@/components/icons/IconLineSpacing.vue';
    import { useTextField } from '@/composables/useTextField';
    import MobileColorPicker from '@/components/MobileColorPicker.vue';
    import TextIcon from '@/components/icons/TextIcon.vue';
    import MobileTextAlign from '@/components/MobileTextAlign.vue';
    import MobilePopUp from '@/components/MobilePopUp.vue';
    import MobileFontSelector from '@/components/MobileFontSelector.vue';
    import MobileColorChart from '@/components/MobileColorChart.vue';
    import MobileSpacingControl from '@/components/MobilSpacingControl.vue';
    import { useConfigStore } from '@/store/config.store';
    import { dataStore } from '@/commons/DataStore';
    import { toRef, ref, useTemplateRef, onMounted, onUnmounted } from 'vue';
    import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/solid';
    import { useScroll } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';

    const { field } = defineProps<{ field: ITextboxField }>();

    const { currentSide, isColorsLocked } = storeToRefs(useConfigStore());
    const fabricWorld = dataStore.getFabricWorld(currentSide.value);

    const fieldRef = toRef(() => field);
    const toolbarContainerRef = useTemplateRef('toolbarContainer');
    const { arrivedState, x } = useScroll(toolbarContainerRef, { behavior: 'smooth' });
    type PopupType = 'characterSpacing' | 'color' | 'font' | 'lineSpacing';

    const activePopup = ref<PopupType | null>(null);

    function togglePopup(type: PopupType) {
        activePopup.value = activePopup.value === type ? null : type;
    }

    function closePopup() {
        activePopup.value = null;
    }

    function scrollLeft() {
        x.value = 0;
    }

    function scrollRight() {
        x.value = toolbarContainerRef.value?.clientWidth || 0;
    }

    const { align, angle, color, fontSize, isBold, isItalic, isUnderlined } = useTextField(fieldRef, fabricWorld);

    onMounted(() => {
        window.visualViewport?.addEventListener('resize', scrollDown);
    });

    onUnmounted(() => {
        // trigger resize to center text
        window.visualViewport?.removeEventListener('resize', scrollDown);
    });
    function scrollDown() {
        if (!toolbarContainerRef.value || !window.visualViewport) {
            return;
        }
        window.scrollBy(0, toolbarContainerRef.value.getBoundingClientRect().bottom - window.visualViewport.height);
    }
</script>
<template>
    <div class="absolute bottom-0 z-20 h-20 w-screen bg-white shadow-mobile-bar">
        <div
            ref="toolbarContainer"
            class="flex h-full w-full flex-row items-center gap-2 overflow-x-auto px-5"
        >
            <BaseButton
                disable-hover
                class="h-12 w-12 flex-none"
                @click="togglePopup('font')"
            >
                <span class="sr-only">{{ $t('font_selector') }}</span>
                <TextIcon />
            </BaseButton>
            <BaseInput
                v-model="fontSize"
                name="font_size"
                :type="'number'"
                input-mode="numeric"
                :label="$t('font_size')"
                :show-label="false"
                class="flex h-12 w-14 flex-none items-center justify-center rounded-none border border-secondary pl-1"
            />
            <MobileColorPicker
                v-if="!isColorsLocked"
                v-model="color"
                class="flex-none"
                @click="togglePopup('color')"
            />
            <BaseInput
                v-model="angle"
                name="angle"
                :type="'number'"
                :show-label="false"
                :label="$t('angle')"
                class="flex h-12 w-14 flex-none items-center justify-center rounded-none border border-border-primary pl-1"
            >
                <span class="absolute -top-1 right-1 h-2">°</span>
            </BaseInput>
            <BaseToggleButton
                v-model="isBold"
                disable-hover
                :bordered="false"
            >
                <span class="sr-only"> {{ $t('bold') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl font-bold">
                    {{ $t('bold_symbol') }}
                </span>
            </BaseToggleButton>
            <BaseToggleButton
                v-model="isItalic"
                disable-hover
                :bordered="false"
            >
                <span class="sr-only"> {{ $t('italic') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl italic">
                    {{ $t('italic_symbol') }}
                </span>
            </BaseToggleButton>
            <BaseToggleButton
                v-model="isUnderlined"
                disable-hover
                :bordered="false"
            >
                <span class="sr-only"> {{ $t('underline') }} </span>
                <span class="flex h-full w-full items-center justify-center font-serif text-xl underline">
                    {{ $t('underline_symbol') }}
                </span>
            </BaseToggleButton>

            <IconCharacterSpacing
                class="h-6 w-6 flex-none"
                @click="togglePopup('characterSpacing')"
            >
                <span class="sr-only">{{ $t('character_spacing') }}</span>
            </IconCharacterSpacing>

            <IconLineSpacing
                class="h-6 w-6 flex-none"
                @click="togglePopup('lineSpacing')"
            >
                <span class="sr-only">{{ $t('line_spacing') }}</span>
            </IconLineSpacing>
            <MobileTextAlign
                v-model="align"
                class="flex-none"
            />
        </div>
        <transition
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            :duration="200"
        >
            <div
                v-if="!arrivedState.left"
                class="pointer-events-none absolute bottom-0 left-0 z-10 flex h-20 w-48 items-center bg-gradient-to-l from-transparent to-white pl-5"
            >
                <BaseButton
                    class="pointer-events-auto h-12 w-8 flex-none"
                    variant="filled"
                    disable-hover
                    @click="scrollLeft"
                >
                    <span class="sr-only">{{ $t('scroll_left') }}</span>
                    <ChevronLeftIcon class="w-6" />
                </BaseButton>
            </div>
        </transition>
        <transition
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            :duration="200"
        >
            <div
                v-if="!arrivedState.right"
                class="pointer-events-none absolute bottom-0 right-0 z-10 flex h-20 w-48 items-center justify-end bg-gradient-to-r from-transparent to-white pr-5"
            >
                <BaseButton
                    class="pointer-events-auto h-12 w-8 flex-none"
                    variant="filled"
                    disable-hover
                    @click="scrollRight"
                >
                    <span class="sr-only">{{ $t('scroll_left') }}</span>
                    <ChevronRightIcon class="w-6" />
                </BaseButton>
            </div>
        </transition>
        <MobilePopUp
            :show="activePopup === 'font'"
            @close="closePopup"
        >
            <MobileFontSelector
                :field
                @close="closePopup"
            />
        </MobilePopUp>

        <MobilePopUp
            :show="activePopup === 'color'"
            @close="closePopup"
        >
            <MobileColorChart
                v-model="color"
                @close="closePopup"
            />
        </MobilePopUp>

        <MobilePopUp
            :show="activePopup === 'characterSpacing'"
            @close="closePopup"
        >
            <MobileSpacingControl
                :field
                :title="$t('character_spacing')"
                spacing-type="characterSpacing"
                min="0"
                max="50"
                @close="closePopup"
            />
        </MobilePopUp>

        <MobilePopUp
            :show="activePopup === 'lineSpacing'"
            @close="closePopup"
        >
            <MobileSpacingControl
                :field
                :title="$t('line_spacing')"
                spacing-type="lineSpacing"
                min="-16"
                max="50"
                @close="closePopup"
            />
        </MobilePopUp>
    </div>
</template>
